import { createThunk, model, Model } from 'mst-collection';
import { getRoot, types } from 'mobx-state-tree';
import { format, parseJSON } from 'date-fns';

import { enumerationNumber } from '@models/types';
import {
  Api,
  IChangeTariffPlanRequestParams,
  IConfirmChangeTariffPlanRequestParams,
} from '@api';
import {
  PaymentMethodEnum,
  TariffPlanCodeEnum,
  TariffPlanStatusEnum,
} from '@constants';
import type { RootModelType } from '../RootModel';

export class UserTariffPlan extends Model({
  id: enumerationNumber([
    TariffPlanCodeEnum.Trial,
    TariffPlanCodeEnum.Monthly,
    TariffPlanCodeEnum.Quarterly,
    TariffPlanCodeEnum.Yearly,
  ]),
  price: types.string,
  endTime: types.string,
  startTime: types.string,
  status: enumerationNumber([
    TariffPlanStatusEnum.Active,
    TariffPlanStatusEnum.Inactive,
    TariffPlanStatusEnum.Canceled,
  ]),
  method: enumerationNumber([
    PaymentMethodEnum.Stripe,
    PaymentMethodEnum.ApplePay,
    PaymentMethodEnum.GooglePay,
  ]),
}) {
  get isActive() {
    return (
      this.status === TariffPlanStatusEnum.Active ||
      this.status === TariffPlanStatusEnum.Canceled
    );
  }

  get isCanceled() {
    return this.status === TariffPlanStatusEnum.Canceled;
  }

  get canBeCanceled() {
    return this.status === TariffPlanStatusEnum.Active;
  }

  get formattedPrice() {
    return `\u00A3\u00A0${this.price}`;
  }

  get formattedStartTime() {
    return format(parseJSON(this.startTime), 'MMMM dd, yyyy');
  }

  get formattedEndTime() {
    return format(parseJSON(this.endTime), 'MMMM dd, yyyy');
  }

  private async _fetchViewerSafe() {
    await getRoot<RootModelType>(this)
      .viewer.fetch.run()
      ?.catch(() => {
        // Do nothing
      });
  }

  private _setStatus(value: TariffPlanStatusEnum) {
    this.status = value;
  }

  cancelSubscription = createThunk(() => {
    return async function cancelSubscription(this: UserTariffPlan) {
      await Api.Payments.cancelSubscription();

      this._setStatus(TariffPlanStatusEnum.Canceled);
    };
  });

  changeTariffPlan = createThunk(
    (params: IChangeTariffPlanRequestParams) => {
      return async function changeTariffPlan(this: UserTariffPlan) {
        const res = await Api.Payments.changeTariffPlan(params);

        if (res.data.requiredAction) {
          return res.data;
        }

        await this._fetchViewerSafe();

        return res.data;
      };
    },
  );

  confirmChangeTariffPlan = createThunk(
    (params: IConfirmChangeTariffPlanRequestParams) => {
      return async function confirmChangeTariffPlan(
        this: UserTariffPlan,
      ) {
        await Api.Payments.confirmChangeTariffPlan(params);

        await this._fetchViewerSafe();
      };
    },
  );
}

export const UserTariffPlanModel = model(UserTariffPlan);
